import React, { useState } from "react";
import { Box, Popover, Tooltip, Typography } from "@mui/material";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import { EllipsisTooltipText, IconContainerCircle } from "../../components-new/common";
import { DeleteIcon, EditIcon, SuiteFolderIcon } from "../../components-new/icons";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ChildCheckBox from "./ChildCheckbox";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SettingsBackupRestoreOutlinedIcon from "@mui/icons-material/SettingsBackupRestoreOutlined";
import { useNavigate } from "react-router-dom";

export default function TestSuiteCard({
  width,
  isFolder = false,
  item,
  handleSuiteClick,
  handleDeleteClick,
  testSuiteId = null,
  handleSettings,
  handleReNameSuite,
  addTestCase,
  getSelectionKey,
  caseToBeAdded,
  suiteTitle,
  selectedProdId,
  showDeletedTestCases,
  showDeletedSuites = false,
  handleRestoreClick,
  handleRestoreSuite,
  isRestoringSuite = false,
  isSuiteInsideSuite = false,
  isReadOnly = false,
}) {
  const navigate = useNavigate();

  const [suitesAnchorEl, setSuitesAnchorEl] = useState(null);
  const suitesOpen = Boolean(suitesAnchorEl);

  const suites = [
    ...new Set(
      (item?.testSuiteNames ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
  const hasSuites = suites.length > 0;

  const handleSuitesOpen = (e) => {
    e.stopPropagation();
    setSuitesAnchorEl(e.currentTarget);
  };

  const handleSuitesClose = () => {
    setSuitesAnchorEl(null);
  };

  const handleViewTestCase = () => {
    const moduleId = item.Module;
    const productId = item.Product || item.idproducts || selectedProdId;

    navigate("/new/test-studio", {
      state: {
        fromExecutionReports: true,
        idproducts: productId,
        idmodules: moduleId,
        idtest_case: item.idtest_case,
        Test_Case: item.Test_Case,
      },
    });
  };

  return (
    <Box width={width} display="flex" flexDirection="column" position="relative" pt="16px">
      {isFolder && (
        <Box position="absolute" top="1px">
          <SuiteFolderIcon />
        </Box>
      )}
      <Box
        p={"20px 20px 15px"}
        bgcolor={"#ffffff"}
        border="1px solid #D1DBFF"
        borderRadius={"6px"}
        sx={{
          borderTopLeftRadius: isFolder ? 0 : "6px",
          cursor: isFolder && showDeletedSuites ? "default" : "pointer",
        }}
        onClick={() => {
          if (isFolder && showDeletedSuites) return;
          if (isFolder) {
            handleSuiteClick(item.idtest_suite);
            return;
          }
          handleViewTestCase();
        }}
      >
        <Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
          {!showDeletedTestCases && !showDeletedSuites && !isReadOnly && (
            <ChildCheckBox
              addTestCase={addTestCase}
              caseToBeAdded={caseToBeAdded}
              item={item}
              getSelectionKey={getSelectionKey}
            />
          )}
          <Tooltip
            title={isFolder ? item.Test_Suite : item.Test_Case || "Test Case"}
            placement="top"
            arrow
          >
            <Typography
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
                lineHeight: "1.3em",
                minHeight: "2.6em",
                flex: 1,
                textDecoration: showDeletedTestCases || showDeletedSuites ? "line-through" : "none",
                color: showDeletedTestCases || showDeletedSuites ? "#9e9e9e" : "#2B3539",
              }}
            >
              {isFolder ? item.Test_Suite : item.Test_Case || "Test Case"}
            </Typography>
          </Tooltip>
        </Box>
        <Box mt={"20px"} display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            {isFolder && (
              <>
                <Typography fontWeight={600} color="#2B3539">
                  {item.countOfTestCases || 0}
                </Typography>
                <Typography color="#6A6A6A" fontSize="12px">
                  Test Cases
                </Typography>
              </>
            )}
          </Box>

          <Box display="flex" alignItems="center" columnGap={2}>
            {isSuiteInsideSuite ? (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(e, item);
                }}
              >
                <IconContainerCircle tooltipText="Remove Suite from Test Suite">
                  <DeleteIcon />
                </IconContainerCircle>
              </Box>
            ) : (
              <>
                {isFolder && !showDeletedSuites && (
                  <>
                    <Box onClick={handleReNameSuite}>
                      <IconContainerCircle tooltipText="Rename Test Suite">
                        <EditIcon />
                      </IconContainerCircle>
                    </Box>
                    <Box onClick={handleSettings}>
                      <IconContainerCircle tooltipText="Test Suite Settings">
                        <SettingsOutlinedIcon />
                      </IconContainerCircle>
                    </Box>
                  </>
                )}
                {(testSuiteId || isFolder) && !showDeletedSuites && (
                  <Box onClick={showDeletedTestCases ? handleViewTestCase : handleDeleteClick}>
                    <IconContainerCircle
                      tooltipText={isFolder ? "Delete Test Suite" : "Remove from Suite"}
                    >
                      <DeleteIcon />
                    </IconContainerCircle>
                  </Box>
                )}

                {(testSuiteId || !isFolder) && (
                  <Box
                    onClick={(e) => {
                      if (showDeletedTestCases) {
                        if (isReadOnly) return;
                        handleRestoreClick(e);
                      } else {
                        handleViewTestCase(e);
                      }
                    }}
                    sx={{
                      opacity: showDeletedTestCases && isReadOnly ? 0.5 : 1,
                      cursor: showDeletedTestCases && isReadOnly ? "not-allowed" : "pointer",
                    }}
                  >
                    <IconContainerCircle
                      tooltipText={showDeletedTestCases ? "UnDelete Test Case" : "View Test Case"}
                    >
                      {showDeletedTestCases ? <DeleteIcon /> : <VisibilityIcon />}
                    </IconContainerCircle>
                  </Box>
                )}

                {!isFolder && !showDeletedTestCases && !testSuiteId && hasSuites && (
                  <Box
                    onMouseEnter={handleSuitesOpen}
                    onMouseLeave={handleSuitesClose}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconContainerCircle tooltipText="">
                      <LayersOutlinedIcon sx={{ fontSize: "18px", color: "#2B3539" }} />
                    </IconContainerCircle>
                  </Box>
                )}

                {!isFolder && !showDeletedTestCases && !testSuiteId && hasSuites && (
                  <Popover
                    open={suitesOpen}
                    anchorEl={suitesAnchorEl}
                    onClose={handleSuitesClose}
                    anchorOrigin={{ vertical: "center", horizontal: "right" }}
                    transformOrigin={{ vertical: "center", horizontal: "left" }}
                    disableRestoreFocus
                    sx={{ pointerEvents: "none" }}
                    slotProps={{
                      paper: {
                        sx: {
                          borderRadius: "8px",
                          border: "1px solid rgba(209, 219, 255, 0.6)",
                          boxShadow: "0 4px 16px rgba(43, 53, 57, 0.12)",
                          ml: 0.5,
                          backgroundColor: "rgba(255, 255, 255, 0)",
                          backdropFilter: "blur(8px)",
                          WebkitBackdropFilter: "blur(8px)",
                        },
                      },
                    }}
                  >
                    <Box p={1.5} minWidth={170} maxHeight={220} sx={{ overflowY: "auto" }}>
                      <Typography
                        fontSize={11}
                        fontWeight={600}
                        color="#6A6A6A"
                        mb={0.75}
                        sx={{ textTransform: "uppercase", letterSpacing: "0.4px" }}
                      >
                        In Test Suites
                      </Typography>
                      {suites.map((s, i) => {
                        const label = typeof s === "string" ? s : s?.Test_Suite ?? "Unnamed suite";
                        return (
                          <Box
                            key={s?.idtest_suite ?? i}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            py={0.5}
                          >
                            <LayersOutlinedIcon sx={{ fontSize: "15px", color: "#5541D7" }} />
                            <Typography fontSize={13} color="#2B3539">
                              {label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Popover>
                )}
                {isFolder && showDeletedSuites && (
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isRestoringSuite || isReadOnly) return;
                      handleRestoreSuite?.(e, item);
                    }}
                    sx={{
                      opacity: isReadOnly ? 0.5 : 1,
                      cursor: isReadOnly ? "not-allowed" : "pointer",
                    }}
                  >
                    <IconContainerCircle
                      tooltipText={isRestoringSuite ? "Restoring..." : "Restore Test Suite"}
                    >
                      <SettingsBackupRestoreOutlinedIcon
                        sx={{
                          color: isRestoringSuite ? "#9AA0B5" : "#2B3539",
                          fontSize: "18px",
                        }}
                      />
                    </IconContainerCircle>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
